import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { Button, DocumentController } from '@/components/Shared/ui';
import { stringToCamelCase } from '@/services';
import { useAppSelector } from '@/store';
import type { FileType } from '@/types/uploadFiles';

import { FILE_NAMES } from '../../constants';
import { GrnFactoryCheck } from '../../entities';
import useInstructionsText from '../../hooks/useInstructionsText';

interface IProps {
  isSubmitLoading: boolean;
  onSubmit: (values: Record<string, FileType>) => void;
}

const SolvaBank: React.FC<IProps> = ({ isSubmitLoading, onSubmit }) => {
  const { selectedBank, attachedFiles, isGrnFactoryCheckPassed } =
    useAppSelector((state) => state.financingStep);

  useInstructionsText();

  const files = useMemo(() => {
    if (!attachedFiles) return [];
    return attachedFiles
      .map((item) => ({
        ...item,
        type: stringToCamelCase(item.type)
      }))
      .filter((value, index, self) => {
        return (
          self.findIndex((item) => item.type === value.type) === index &&
          (value.type === 'guaranteeSolva' ||
            value.type === 'spouseConsent' ||
            value.type === 'techPassport')
        );
      });
  }, [attachedFiles]);

  const {
    control,
    formState: { errors },
    handleSubmit
  } = useForm<{ spouseConsent: FileType }>({
    mode: 'all',
    delayError: 200
  });

  const prepareSubmit = (values: Record<string, FileType>) => {
    onSubmit(values);
  };

  return (
    <form
      className="financing-step__form"
      onSubmit={handleSubmit(prepareSubmit)}
    >
      {files
        ? files.map((file) => (
            <React.Fragment key={file.type}>
              <DocumentController
                fileLink={file.uuid}
                title={FILE_NAMES[file.type as keyof typeof FILE_NAMES]}
                downloadable
              />
              {file.type === 'guaranteeSolva' && (
                <GrnFactoryCheck
                  grnFileLink={file.link}
                  grnFileType={file.type}
                />
              )}
            </React.Fragment>
          ))
        : selectedBank?.status.code === 'financed' && (
            <p className="financing-step__no-documents">Нет документов</p>
          )}

      {selectedBank?.status.code !== 'financed' && (
        <>
          <DocumentController
            control={control}
            name="spouseConsent"
            title={
              !files.some((item) => item.type === 'spouseConsent')
                ? 'Согласие супруга(и) / Отсутствие супруга(и)'
                : 'Переотправить Согласие супруга(и) / Отсутствие супруга(и)'
            }
            uploadable
            isRequired
          />

          <Button
            className="financing-step__submit"
            variant="filled-red"
            disabled={
              Object.values(errors).length !== 0 || !isGrnFactoryCheckPassed
            }
            isLoading={isSubmitLoading}
          >
            Отправить документы
          </Button>
        </>
      )}
    </form>
  );
};

export default SolvaBank;
